import { AutoFocus } from 'primeng/autofocus';

/**
 * Neutralise un défaut de `primeng/autofocus` (PrimeNG 21.2).
 *
 * La directive teste `this.autofocus === false` pour décider de retirer
 * l'attribut HTML `autofocus`. Or `p-button` lui transmet
 * `[pAutoFocus]="autofocus || buttonProps?.autofocus"` : quand l'application ne
 * demande rien, la valeur est `undefined`, pas `false`. Le test échoue, la
 * branche `else` s'exécute, et CHAQUE bouton et sélecteur de l'écran se
 * retrouve avec `autofocus="true"`.
 *
 * Conséquence observée : à l'ouverture d'une page, le navigateur donne le focus
 * au premier de ces éléments — en pratique le bouton « Options » d'une liste.
 * Un anneau de focus bleu apparaît sans que personne n'ait rien fait, et un
 * lecteur d'écran démarre au milieu de la page au lieu de son début.
 *
 * On rétablit la sémantique attendue : seule une valeur vraie pose l'attribut.
 * Le correctif est idempotent et se désarme tout seul si PrimeNG corrige la
 * comparaison en amont.
 */
export function patchPrimeNgAutoFocus(): void {
  const proto = AutoFocus?.prototype as
    | { onAfterContentChecked?: () => void; autofocus?: unknown }
    | undefined;
  const original = proto?.onAfterContentChecked;
  if (!proto || typeof original !== 'function') return;
  if ((original as { __icakenePatched?: boolean }).__icakenePatched) return;

  function patched(this: { autofocus?: unknown }) {
    // `undefined` et `null` valent « pas d'autofocus », comme `false`.
    if (this.autofocus == null) this.autofocus = false;
    return original!.call(this);
  }
  (patched as { __icakenePatched?: boolean }).__icakenePatched = true;
  proto.onAfterContentChecked = patched;
}
